<script setup>
import { onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
  title: { type: String, default: '' },
  text: { type: String, default: '' },
  ctaHref: { type: String, default: '/' },
  ctaLabel: { type: String, default: 'Bekijk aanbod' },
  iconSrc: { type: String, default: '' },
  iconAlt: { type: String, default: '' },
  iconTitle: { type: String, default: '' }
});

const isOpen = ref(false);
const rootRef = ref(null);
const panelRef = ref(null);
const iconRef = ref(null);

function openPopup() {
  isOpen.value = true;
}

function closePopup(event) {
  event?.preventDefault();
  isOpen.value = false;
}

function onDocumentClick(event) {
  if (!isOpen.value) return;
  const target = event.target;
  if (panelRef.value?.contains(target) || iconRef.value?.contains(target)) {
    return;
  }
  isOpen.value = false;
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
});
</script>

<template>
  <div
    ref="rootRef"
    class="vsb-promo-popup"
    :class="{ 'vsb-promo-popup--open': isOpen }"
  >
    <div ref="panelRef" class="vsb-promo-popup__panel shadow" @click.stop>
      <div class="vsb-promo-popup__body">
        <div class="vsb-promo-popup__title">{{ title }}</div>
        <p v-if="text" class="vsb-promo-popup__text">{{ text }}</p>
        <div class="vsb-promo-popup__actions">
          <a :href="ctaHref" class="vsb-promo-popup__cta">{{ ctaLabel }}</a>
        </div>
      </div>
      <button
        type="button"
        class="vsb-promo-popup__close"
        aria-label="Sluiten"
        @click="closePopup"
      >
        &times;
      </button>
    </div>

    <button
      ref="iconRef"
      type="button"
      class="vsb-promo-popup__fab shadow"
      :title="iconTitle"
      aria-label="Promotie openen"
      @click.stop="openPopup"
    >
      <img v-if="iconSrc" :src="iconSrc" :alt="iconAlt" class="vsb-promo-popup__fab-img" />
    </button>
  </div>
</template>

<style scoped>
@import '../../shared/vsb-brand-colors.css';

.vsb-promo-popup {
  position: fixed;
  bottom: 25px;
  right: 25px;
  z-index: 9999999;
}

.vsb-promo-popup__panel {
  position: absolute;
  right: 15px;
  bottom: 15px;
  background: linear-gradient(135deg, var(--vsb-blue-dark) 0%, var(--vsb-blue-mid) 100%);
  color: #fff;
  padding: 20px 40px 20px 20px;
  border-radius: 8px;
  width: 270px;
  max-width: calc(100vw - 55px);
  display: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 12px 32px rgba(11, 61, 145, 0.35);
}

.vsb-promo-popup--open .vsb-promo-popup__panel {
  display: block;
}

.vsb-promo-popup__title {
  font-weight: 700;
  font-size: 1.2em;
  margin: 0 0 8px;
  line-height: 1.3;
}

.vsb-promo-popup__text {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.92);
}

.vsb-promo-popup__actions {
  margin-top: 10px;
}

.vsb-promo-popup__cta {
  display: inline-block;
  background: #ffe001;
  color: #1a2e44;
  border: none;
  font-weight: 600;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.8rem;
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(11, 61, 145, 0.2);
  transition: background 0.2s, transform 0.2s;
}

.vsb-promo-popup__cta:hover {
  background: #fff;
  color: var(--vsb-blue-dark);
  transform: translateY(-1px);
}

.vsb-promo-popup__close {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 32px;
  height: 32px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: var(--vsb-blue-mid);
  color: #fff;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(11, 61, 145, 0.25);
  transition: background 0.2s;
}

.vsb-promo-popup__close:hover {
  background: var(--vsb-blue-dark);
}

.vsb-promo-popup__fab {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--vsb-blue-mid);
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 4px 14px rgba(11, 61, 145, 0.35);
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: vsb-promo-bounce 1.8s infinite;
  transition: background 0.2s, transform 0.2s;
}

.vsb-promo-popup__fab:hover {
  background: var(--vsb-blue-dark);
}

.vsb-promo-popup__fab-img {
  width: 90%;
  height: 90%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
}

@keyframes vsb-promo-bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-6px); }
}

@media only screen and (max-width: 768px) {
  .vsb-promo-popup {
    bottom: 15px;
    right: 15px;
  }

  .vsb-promo-popup__panel {
    width: 250px;
    padding: 15px 30px 15px 15px;
  }

  .vsb-promo-popup__fab {
    width: 50px;
    height: 50px;
  }
}
</style>
